/**
 * Website Context Service
 * Provides AI with comprehensive knowledge about the website's content,
 * services, contact information, and capabilities
 */

class WebsiteContextService {
    constructor() {
        this.websiteInfo = {
            // Basic Information
            name: "Spoon & Spice",
            tagline: "Your Premier Food Delivery Platform",
            description: "A comprehensive food delivery platform connecting customers with local restaurants and vendors",
            
            // Contact Information
            contact: {
                email: "connect.spoonandspice@gmail.com",
                phone: "+91-7483423925",
                whatsapp: "+91-7483423925",
                address: "#2, Gokulappa Building, 20th 'L' Cross Rama Temple Road, Post, Viveknagar, Ejipura, Bengaluru, Karnataka 560047",
                businessHours: {
                    support: "24/7",
                    delivery: "10:00 AM - 11:00 PM",
                    customerService: "9:00 AM - 10:00 PM"
                }
            },
            
            // Services Offered
            services: {
                foodDelivery: {
                    name: "Food Delivery",
                    description: "Order from Spoon And Spice and get food delivered to your doorstep",
                    features: ["Real-time tracking", "Multiple payment options", "Fast delivery"],
                    deliveryTime: "Varies on the order date and time",
                    deliveryFee: "Starting from ₹25",
                    minimumOrder: "₹100"
                },
                catering: {
                    name: "Catering Services",
                    description: "Professional catering for events, parties, and corporate functions",
                    features: ["Custom menus", "Bulk orders", "Event planning", "Professional service"],
                    advanceBooking: "24-48 hours minimum",
                    pricing: "Custom quotes based on requirements"
                },
                vendorOnboarding: {
                    name: "Vendor Onboarding",
                    description: "Join our platform as a restaurant or food vendor",
                    features: ["Easy registration", "Marketing support", "Order management", "Payment processing"],
                    commission: "Competitive rates",
                    support: "Dedicated vendor support team"
                }
            },
            
            // Payment Options
            paymentMethods: {
                online: ["Credit/Debit Cards", "Net Banking", "UPI", "Digital Wallets"],
                offline: ["Cash on Delivery", "Card on Delivery"],
                security: "SSL encrypted, secure payment processing"
            },
            
            // Delivery Areas
            deliveryAreas: {
                primary: ["Bangalore"],
                coverage: "As of now we server in Bangalore India",
                expansion: "Continuously expanding to new areas"
            },
            
            // Policies
            policies: {
                refund: {
                    eligibility: "Eligible if the order is cancelled 48 hours before the order date",
                    process: "Contact support for refund requests",
                    timeline: "3-5 business days for processing"
                },
                cancellation: {
                    beforePreparation: "Free cancellation if the order is cancelled 48 hours before the order date",
                    afterPreparation: "Contact support for assistance",
                    timeline: "Up to 10 minutes after order placement if the order is cancelled 48 hours before the order date"
                },
                privacy: {
                    dataProtection: "Indian data protection compliant",
                    dataUsage: "Only for service improvement and order processing",
                    sharing: "Never shared with third parties without consent"
                }
            },
            
            // Common Issues & Solutions
            commonIssues: {
                orderNotDelivered: {
                    solution: "Contact support immediately with order ID",
                    escalation: "Refund or re-delivery arranged within 2 hours"
                },
                wrongOrder: {
                    solution: "Report within 15 minutes of delivery",
                    resolution: "Correct order delivered free of charge"
                },
                paymentIssues: {
                    solution: "Check payment method and try again",
                    support: "Contact support for payment assistance"
                },
                deliveryDelay: {
                    solution: "Track order in real-time",
                    compensation: "Delivery fee waived for delays over 1 hour"
                }
            },
            
            // Account Management
            accountFeatures: {
                registration: "Free account creation",
                profile: "Manage personal information and preferences",
                orderHistory: "View past orders and reorder",
                favorites: "Save favorite restaurants and dishes",
                addresses: "Multiple delivery addresses",
                loyalty: "Earn points with every order"
            },
            
            // Technical Support
            technicalSupport: {
                appIssues: "Clear cache and restart app",
                websiteIssues: "Try different browser or incognito mode",
                loginProblems: "Reset password or contact support",
                orderTracking: "Use order ID to track in app or website"
            }
        };
        
        this.faqData = [
            {
                question: "How do I place an order?",
                answer: "Simply browse restaurants, select items, add to cart, choose delivery address, select payment method, and confirm your order. You'll receive real-time updates on your order status."
            },
            {
                question: "What are the delivery charges?",
                answer: "Delivery charges start from ₹25 and vary based on distance and restaurant. Some restaurants offer free delivery on orders above ₹300."
            },
            {
                question: "How long does delivery take?",
                answer: "Standard delivery time is 30-60 minutes depending on your location and restaurant preparation time. You can track your order in real-time."
            },
            {
                question: "Can I cancel my order?",
                answer: "Yes, you can cancel your order for free within 10 minutes of placement. After that, contact our support team for assistance."
            },
            {
                question: "What payment methods do you accept?",
                answer: "We accept all major credit/debit cards, UPI, digital wallets, net banking, and cash on delivery."
            },
            {
                question: "How do I track my order?",
                answer: "You can track your order in real-time through the app or website using your order ID. You'll also receive SMS updates."
            },
            {
                question: "What if my order is wrong or missing items?",
                answer: "Contact our support team immediately with your order ID. We'll arrange for the correct items to be delivered free of charge."
            },
            {
                question: "Do you offer refunds?",
                answer: "Yes, we offer refunds for orders that don't meet our quality standards. Refund requests must be made within 30 minutes of delivery."
            },
            {
                question: "How do I become a vendor on your platform?",
                answer: "Visit our vendor registration page, fill out the application form, and our team will contact you within 24 hours to complete the onboarding process."
            },
            {
                question: "What areas do you deliver to?",
                answer: "We currently deliver to major cities across India including Mumbai, Delhi, Bangalore, Chennai, and Hyderabad. We're continuously expanding to new areas."
            }
        ];
    }
    
    /**
     * Get website context for AI
     */
    getWebsiteContext() {
        return {
            ...this.websiteInfo,
            faq: this.faqData,
            lastUpdated: new Date().toISOString()
        };
    }
    
    /**
     * Get relevant information based on user query
     */
    getRelevantInfo(userQuery) {
        const query = userQuery.toLowerCase();
        const relevantInfo = {
            contact: false,
            services: false,
            policies: false,
            faq: false,
            technical: false
        };
        
        // Contact information queries
        if (query.includes('contact') || query.includes('phone') || query.includes('email') || 
            query.includes('address') || query.includes('support') || query.includes('help')) {
            relevantInfo.contact = true;
        }
        
        // Service queries
        if (query.includes('delivery') || query.includes('order') || query.includes('food') || 
            query.includes('restaurant') || query.includes('catering') || query.includes('vendor')) {
            relevantInfo.services = true;
        }
        
        // Policy queries
        if (query.includes('refund') || query.includes('cancel') || query.includes('policy') || 
            query.includes('terms') || query.includes('privacy')) {
            relevantInfo.policies = true;
        }
        
        // FAQ queries
        if (query.includes('how') || query.includes('what') || query.includes('when') || 
            query.includes('where') || query.includes('why') || query.includes('can i')) {
            relevantInfo.faq = true;
        }
        
        // Technical queries
        if (query.includes('app') || query.includes('website') || query.includes('login') || 
            query.includes('password') || query.includes('track') || query.includes('payment')) {
            relevantInfo.technical = true;
        }
        
        return relevantInfo;
    }
    
    /**
     * Get specific information based on query type
     */
    getSpecificInfo(queryType, userQuery = '') {
        switch (queryType) {
            case 'contact':
                return {
                    type: 'contact',
                    data: this.websiteInfo.contact,
                    message: "Here's our contact information:"
                };
                
            case 'services':
                return {
                    type: 'services',
                    data: this.websiteInfo.services,
                    message: "Here are our services:"
                };
                
            case 'policies':
                return {
                    type: 'policies',
                    data: this.websiteInfo.policies,
                    message: "Here are our policies:"
                };
                
            case 'payment':
                return {
                    type: 'payment',
                    data: this.websiteInfo.paymentMethods,
                    message: "Here are our payment options:"
                };
                
            case 'delivery':
                return {
                    type: 'delivery',
                    data: this.websiteInfo.deliveryAreas,
                    message: "Here's our delivery information:"
                };
                
            case 'faq':
                const relevantFaq = this.faqData.filter(faq => 
                    faq.question.toLowerCase().includes(userQuery.toLowerCase()) ||
                    faq.answer.toLowerCase().includes(userQuery.toLowerCase())
                );
                return {
                    type: 'faq',
                    data: relevantFaq.length > 0 ? relevantFaq : this.faqData,
                    message: "Here are some frequently asked questions:"
                };
                
            default:
                return {
                    type: 'general',
                    data: this.websiteInfo,
                    message: "Here's information about our platform:"
                };
        }
    }
    
    /**
     * Generate context-aware prompt for AI
     */
    generateContextPrompt(userQuery, queryType = 'general') {
        const context = this.getWebsiteContext();
        const specificInfo = this.getSpecificInfo(queryType, userQuery);
        
        return `You are a customer support representative for ${context.name}, a food delivery platform.

WEBSITE CONTEXT:
${JSON.stringify(context, null, 2)}

USER QUERY: "${userQuery}"

RELEVANT INFORMATION:
${JSON.stringify(specificInfo, null, 2)}

INSTRUCTIONS:
1. Provide accurate information based on the website context above
2. Only use information that exists in the context
3. Be helpful and professional
4. If the user asks about something not in the context, politely redirect them to contact support
5. For order-related queries, guide them through the process
6. Always provide contact information when relevant
7. Use the exact contact details, policies, and information provided in the context

Please provide a helpful response based on the user's query and the website context.`;
    }
    
    /**
     * Check if query is order-related
     */
    isOrderRelatedQuery(query) {
        const orderKeywords = [
            'order', 'place order', 'buy', 'purchase', 'add to cart', 'checkout',
            'delivery', 'track order', 'order status', 'cancel order', 'modify order'
        ];
        
        return orderKeywords.some(keyword => query.toLowerCase().includes(keyword));
    }
    
    /**
     * Get order processing guidance
     */
    getOrderProcessingGuidance() {
        return {
            steps: [
                "1. Browse restaurants and select your preferred cuisine",
                "2. Add items to your cart by clicking 'Add to Cart'",
                "3. Review your cart and click 'Proceed to Checkout'",
                "4. Select or add your delivery address",
                "5. Choose your payment method (Card, UPI, Cash on Delivery)",
                "6. Confirm your order and wait for confirmation",
                "7. Track your order in real-time through the app"
            ],
            tips: [
                "Minimum order value is ₹100",
                "Delivery time is typically 30-60 minutes",
                "You can cancel orders within 10 minutes of placement",
                "Contact support if you need to modify your order"
            ],
            contact: "For order assistance, contact us at connect.spoonandspice@gmail.com or +91-7483423925"
        };
    }
}

// Create singleton instance
const websiteContextService = new WebsiteContextService();

export default websiteContextService;
